import { KText } from '@/ui-library/components';
import { Box, Flex } from '@chakra-ui/react';
import { useMemo } from 'react';
import { Button } from '@kula-ai/components';
import { Edit05, Download01 } from '@kula-ai/icons-core';

interface EmptyTokensWarningProps {
  nonFilledValidTokens: string[];
  invalidTokens: string[];
  detailsFormConfig: any[];
  onGoToDetails?: () => void;
  onDownload: () => void;
  ReplacementUploader: React.ComponentType<{ isLoading: boolean }>;
  isUploadLoading: boolean;
}

export const EmptyTokensWarning = ({
  nonFilledValidTokens,
  invalidTokens,
  detailsFormConfig,
  onGoToDetails,
  onDownload,
  ReplacementUploader,
  isUploadLoading,
}: EmptyTokensWarningProps) => {
  const missingFieldLabels = useMemo(() => {
    const tokenSet = new Set([...nonFilledValidTokens, ...invalidTokens]);
    return detailsFormConfig
      .flatMap((section) => section?.fields ?? [section])
      .filter((field) => field?.tokenName && tokenSet.has(field.tokenName))
      .map((field) => field.label);
  }, [nonFilledValidTokens, invalidTokens, detailsFormConfig]);

  return (
    <Box p="24px" mt="12px">
      <KText size="md-semi-bold" color="secondary-900" mb="4px">
        Empty tokens detected in the offer letter template
      </KText>
      <KText size="sm-regular" color="secondary-600" mb="16px">
        Fill the missing information, switch offer letter templates or upload a
        new template to proceed
      </KText>

      {missingFieldLabels.length > 0 && (
        <Box mb="16px">
          <KText size="sm-semi-bold" color="secondary-700" mb="8px">
            Required fields
          </KText>
          <Flex direction="column" gap="4px">
            {missingFieldLabels.map((label, i) => (
              <KText key={i} size="sm-regular" color="secondary-600">
                {label}
              </KText>
            ))}
          </Flex>
        </Box>
      )}

      <Flex gap="8px" mt="8px">
        {onGoToDetails && (
          <Button
            variant="secondary"
            leftElement={<Edit05 size={18} />}
            onClick={onGoToDetails}
          >
            Edit offer
          </Button>
        )}
        <Button
          variant="tertiary"
          onClick={onDownload}
          leftElement={<Download01 size={18} />}
        >
          Download
        </Button>
        <ReplacementUploader isLoading={isUploadLoading} />
      </Flex>
    </Box>
  );
};
